import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ArrowRight } from "lucide-react";
import SEOMeta from "@/components/SEOMeta";
import OptimizedImage from "@/components/OptimizedImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";

// Import all blog images
import blogRoiRenovations from "@/assets/blog-roi-renovations-gta.jpg";
import blogHomeValue from "@/assets/blog-home-value-ontario.jpg";
import blogBuildingPermits from "@/assets/blog-building-permits.jpg";
import blogCommercialHamilton from "@/assets/blog-commercial-hamilton.jpg";
import blogRecentProjects from "@/assets/blog-recent-projects.jpg";
import blogWinterMaterials from "@/assets/blog-winter-materials.jpg";
import blogRecyclableMaterials from "@/assets/blog-recyclable-materials.jpg";
import blogConstructionQuote from "@/assets/blog-construction-quote.jpg";
import blogHiddenCosts from "@/assets/blog-hidden-costs.jpg";
import blogCostsRising from "@/assets/blog-costs-rising.jpg";
import blogWinterConstruction from "@/assets/blog-winter-construction.jpg";
import blogConstructionSeason from "@/assets/blog-construction-season.jpg";
import blogContractorTypes from "@/assets/blog-contractor-types.jpg";
import blogGeneralContractor from "@/assets/blog-general-contractor.jpg";
import blogRenovationTimeline from "@/assets/blog-renovation-timeline.jpg";
import blogConstructionTrends from "@/assets/blog-construction-trends-2025.jpg";
import blogOntarioRebates from "@/assets/blog-ontario-rebates.jpg";

const blogPosts = [
  // ROI & Value
  {
    id: 1,
    title: "How to Get the Best ROI on Renovations in the GTA",
    category: "ROI & Value",
    description: "Discover which renovation projects deliver the highest return on investment in Greater Toronto Area homes.",
    slug: "best-roi-renovations-gta",
    image: blogRoiRenovations,
    imageAlt: "Before and after home renovation showing ROI improvements in GTA",
  },
  {
    id: 2,
    title: "Which Home Renovations Add the Most Value in Ontario?",
    category: "ROI & Value",
    description: "Learn which home improvements provide the best value increase for Ontario homeowners looking to sell.",
    slug: "home-renovations-value-ontario",
    image: blogHomeValue,
    imageAlt: "Beautiful Ontario home with value-adding renovations",
  },
  
  // Permits & Regulations
  {
    id: 3,
    title: "Where Building Permits Are Hardest to Get (and How to Speed It Up)",
    category: "Permits & Planning",
    description: "Navigate Ontario's building permit process and learn strategies to expedite approval in challenging municipalities.",
    slug: "building-permits-ontario-guide",
    image: blogBuildingPermits,
    imageAlt: "Building permit documents and blueprints for Ontario construction",
  },
  {
    id: 4,
    title: "Where to Start When Planning a Commercial Build in Hamilton",
    category: "Permits & Planning",
    description: "A comprehensive guide to launching your commercial construction project in Hamilton, Ontario.",
    slug: "commercial-build-planning-hamilton",
    image: blogCommercialHamilton,
    imageAlt: "Commercial construction site in Hamilton Ontario",
  },
  
  // Project Updates
  {
    id: 5,
    title: "Where We're Building: Recent Projects in Toronto and Niagara",
    category: "Project Updates",
    description: "Take a look at Blue Knight's latest construction projects across Toronto and the Niagara region.",
    slug: "recent-projects-toronto-niagara",
    image: blogRecentProjects,
    imageAlt: "Recent construction projects in Toronto and Niagara region",
  },
  
  // Materials
  {
    id: 6,
    title: "Which Construction Materials Are Best for Canadian Winters?",
    category: "Materials & Methods",
    description: "Find out which building materials stand up to harsh Canadian winters and deliver long-term durability.",
    slug: "construction-materials-canadian-winters",
    image: blogWinterMaterials,
    imageAlt: "Winter-resistant construction materials in snowy conditions",
  },
  {
    id: 7,
    title: "Are Construction Materials Recyclable?",
    category: "Materials & Methods",
    description: "Explore sustainable construction practices and learn which building materials can be recycled or repurposed.",
    slug: "recyclable-construction-materials",
    image: blogRecyclableMaterials,
    imageAlt: "Recyclable construction materials sorted for reuse",
  },
  
  // Costs & Budget
  {
    id: 8,
    title: "What Is Included in a Construction Quote?",
    category: "Costs & Budget",
    description: "Understand what to expect in a professional construction quote and how to compare estimates effectively.",
    slug: "construction-quote-breakdown",
    image: blogConstructionQuote,
    imageAlt: "Detailed construction quote document with itemized costs",
  },
  {
    id: 9,
    title: "What Are the Hidden Costs of Building in the GTA?",
    category: "Costs & Budget",
    description: "Avoid budget surprises by learning about common hidden costs in GTA construction projects.",
    slug: "hidden-costs-building-gta",
    image: blogHiddenCosts,
    imageAlt: "Hidden construction costs at GTA building site",
  },
  {
    id: 10,
    title: "Why Are Construction Costs Rising?",
    category: "Costs & Budget",
    description: "Understand the factors driving construction cost increases and how to budget accordingly.",
    slug: "construction-costs-rising",
    image: blogCostsRising,
    imageAlt: "Construction cost analysis showing rising trends",
  },
  
  // Seasonal & Timing
  {
    id: 11,
    title: "Can You Build During Winter in Canada?",
    category: "Seasonal & Timing",
    description: "Learn about winter construction in Canada, including challenges, solutions, and best practices.",
    slug: "winter-construction-canada",
    image: blogWinterConstruction,
    imageAlt: "Winter construction in Canada with protective measures",
  },
  {
    id: 12,
    title: "When Is Construction Season in Toronto?",
    category: "Seasonal & Timing",
    description: "Plan your project around Toronto's optimal construction season for best results and efficiency.",
    slug: "construction-season-toronto",
    image: blogConstructionSeason,
    imageAlt: "Toronto construction season with active building sites",
  },
  
  // Contractor Selection
  {
    id: 13,
    title: "Which Type of Contractor Do You Need for Your Project?",
    category: "Contractor Selection",
    description: "Determine whether you need a general contractor, specialized tradesperson, or construction manager.",
    slug: "choosing-right-contractor-type",
    image: blogContractorTypes,
    imageAlt: "Different types of contractors on construction site",
  },
  {
    id: 14,
    title: "What Does a General Contractor Actually Do?",
    category: "Contractor Selection",
    description: "Demystify the role of general contractors and understand how they manage construction projects.",
    slug: "general-contractor-role-explained",
    image: blogGeneralContractor,
    imageAlt: "General contractor coordinating construction project",
  },
  
  // Timeline & Process
  {
    id: 15,
    title: "What to Expect During Your Home Renovation Timeline",
    category: "Timeline & Process",
    description: "Set realistic expectations with this detailed breakdown of typical home renovation timelines.",
    slug: "home-renovation-timeline-guide",
    image: blogRenovationTimeline,
    imageAlt: "Home renovation timeline visualization with phases",
  },
  
  // Trends & Rebates
  {
    id: 16,
    title: "What Construction Trends Are Taking Over Ontario in 2025?",
    category: "Trends & Innovation",
    description: "Stay ahead with insights into the latest construction trends shaping Ontario's building industry.",
    slug: "ontario-construction-trends-2025",
    image: blogConstructionTrends,
    imageAlt: "2025 modern construction trends in Ontario",
  },
  {
    id: 17,
    title: "Which Home Upgrades Qualify for Ontario Rebates in 2025?",
    category: "Trends & Innovation",
    description: "Maximize savings by learning which home improvements qualify for Ontario government rebates and incentives.",
    slug: "ontario-home-rebates-2025",
    image: blogOntarioRebates,
    imageAlt: "Ontario home with energy-efficient upgrades qualifying for rebates",
  },
];

const categories = Array.from(new Set(blogPosts.map(post => post.category)));

const Blog = () => {
  return (
    <>
      <SEOMeta
        title="Construction & Renovation Blog | Blue Knight Contracting Ontario"
        description="Expert insights on construction, renovations, permits, costs, and building trends across Ontario. Learn from Blue Knight's experienced contractors."
        keywords="construction blog Ontario, renovation tips GTA, building permits Ontario, construction costs, contractor advice, home renovation guide"
        canonicalUrl="https://blueknightcontracting.ca/blog"
      />
      
      <Header />
      <Breadcrumbs />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Construction & Renovation Insights
              </h1>
              <p className="text-xl opacity-90">
                Expert guidance on building, renovating, and navigating Ontario's construction landscape. 
                From permits to ROI, we cover everything you need to know.
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            {categories.map((category) => (
              <div key={category} className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-foreground border-b border-border pb-3">
                  {category}
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {blogPosts
                    .filter((post) => post.category === category)
                    .map((post) => (
                      <Card key={post.id} className="hover:shadow-lg transition-shadow group overflow-hidden">
                        <OptimizedImage
                          src={post.image}
                          alt={post.imageAlt}
                          className="w-full h-48 object-cover"
                        />
                        <CardHeader>
                          <div className="flex items-center justify-between mb-3">
                            <Badge variant="secondary" className="text-xs">
                              {post.category}
                            </Badge>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>Coming Soon</span>
                            </div>
                          </div>
                          <CardTitle className="text-xl group-hover:text-primary transition-colors">
                            {post.title}
                          </CardTitle>
                          <CardDescription className="line-clamp-2">
                            {post.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Link
                            to={`/blog/${post.slug}`}
                            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
                          >
                            Read More
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-muted py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Our experienced team is here to guide you through every step of your construction or renovation journey.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Get Your Free Quote
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>
      </div>
      
      <Footer />
    </>
  );
};

export default Blog;
